import angular from 'angular';

import userImageTemplate from './userImage.template.html';
import './userImage.css';

export default angular
  .module('csm.ui.userImage', [])

  /*****************************************************************************************
   * userImage */
  /**
* @ngdoc    component
* @module   csm.ui
* @name     csm.ui.userImage.component:userImage
* 
* @param  {String}   height      Maximum image pixel height.
* @param  {String}   width       Maximum image pixel width.
* @param  {String}   src         Image URL.
* @param  {String=}  altSrc      Alternate image URL.
* @param  {Boolean=} noErrorAlt  When **`true`**, no alternate image is substituted if both 
*                                **`src`** and **`altSrc`** cannot be loaded. 
* @param  {Expr=}    onLoad      Callback for image load/error events. **`loaded`** variable
*                                = `true` if the image loaded successfully.
* @param  {String=}  noImageTip  Tooltip text to display when no image is loaded.                                
*
* @description
* Container for showing an image that has been uploaded by a user. User images are 
* displayed in fixed size containers on various web pages. These types of images might 
* include user pictures, website items, icons, logos, etc. 
*
* The image is vertically and horizontally centered within **`height`** and **`width`** 
* and resized down, if necessary, to fit within the dimensions. Aspect ratio is maintained.
* If the image fails to load, a background of a grayed-out camera is displayed unless
* **`noErrorAlt`** is **`true`**.
* 
* If **`alt-src`** is provided, it will be loaded if the load of **`src`** fails. 
* **`on-load`** expression is executed when `src` changes and an attempt is made to load 
* the image. **`loaded`** property is `true` if the image loaded successfully.
*
* @example
  <example module="userImageExample">
    <file name="index.html">
      <div ng-controller="userImageController">
        <form novalidate class="simple-form">
          <label>height:</label>    <input type="number"   ng-model="height" /><br />
          <label>width:</label>     <input type="number"   ng-model="width" /><br />
          <label>noImageTip:</label><input type="string"   ng-model="noImageTip" style="width: 400px" /><br />
          <label>src:</label>       <input type="string"   ng-model="newsrc"     style="width: 400px" /><br />
          <label>altSrc:</label>    <input type="string"   ng-model="newalt"     style="width: 400px" /><br />
          <label>noErrorAlt:</label><input type="checkbox" ng-model="noempty"/>
        </form>
        <div style="color:red" ng-show="!imageLoaded">No image loaded.</div>
        <user-image height="{{height}}" width="{{width}}" 
                       src="{{src}}"  alt-src="{{altSrc}}" no-error-alt="noempty" no-image-tip="{{noImageTip}}"
                   on-load="imageLoaded=loaded" style="border: 1px solid black"></user-image>
      </div>
    </file>
    <file name="script.js">
      angular.module('userImageExample', [ 'csm.ui.userImage' ]).
      controller("userImageController", function ($scope, $sce) 
        {
        $scope.height     = 250;
        $scope.width      = 250;
        $scope.noImageTip = "Please find me an Image!";
        $scope.$watch('newsrc', function(newsrc) { $scope.src =    (newsrc ? $sce.trustAsResourceUrl(newsrc) : newsrc); });
        $scope.$watch('newalt', function(newalt) { $scope.altSrc = (newalt ? $sce.trustAsResourceUrl(newalt) : newalt); });
        });
    </file>
  </example>
*****************************************************************************************/
  .component('userImage', {
    template: userImageTemplate,
    bindings: {
      width: '@',
      height: '@',
      src: '@',
      altSrc: '@',
      noErrorAlt: '<',
      onLoad: '&',
      noImageTip: '@'
    },
    controller: function ($scope, $element, $timeout) {
      var $ctrl = this;

      /*-------------------------------------------------------------*/
      /* onLoadHandler - Set imgLoaded flag and call onLoad handler. */
      /*-------------------------------------------------------------*/
      var onLoadHandler = function (loaded) {
        $ctrl.imgLoaded = loaded;

        if ($ctrl.onLoad) $ctrl.onLoad({ loaded: loaded });
      };

      /*----------------------------------------------------------*/
      /* Add handlers for 'img' element. Use the $timeout         */
      /* service to wait for any current digest cycles to finish. */
      /*----------------------------------------------------------*/
      var img = $element.find('img');
      img.on('load', function () {
        $timeout(function () {
          onLoadHandler(true);
        });
      });
      img.on('error', function () {
        $timeout(function () {
          /*---------------------------------------------------------------*/
          /* Try the alternate image if supplied. Else, call load handler. */
          /*---------------------------------------------------------------*/
          if ($ctrl.src && $ctrl.altSrc && $ctrl.ngSrc != $ctrl.altSrc)
            $ctrl.ngSrc = $ctrl.altSrc;
          else onLoadHandler(false);
        });
      });

      /*-------------------------------------------------------*/
      /* Set ngSrc from src when changed. setting 'img.ng-src' */
      /* to empty string will not change 'img.src' by angular  */
      /* design. So call the handler if new value is empty.    */
      /*-------------------------------------------------------*/
      $scope.$watch('$ctrl.src', function () {
        if (!($ctrl.ngSrc = $ctrl.src)) onLoadHandler(false);
      });
    }
  }).name;
